import { GridSize } from '@mui/material';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useBuildEmobilityClgLink, useResponsive } from '../../utils/customHooks';
import { Prefixer } from '../../utils/utilityTypes';
import { TilesContainer } from '../tilesContainer';
import { commonNamespace, ctaType } from './constants';
import { EmobilityNavigationTile, EmobilityNavigationTileClasses } from './emobilityNavigationTile';

type MappedTileClassNames = Prefixer<EmobilityNavigationTileClasses, 'tile'>;

const useStyles = makeStyles({ name: 'EmobilityNavigation' })((theme) => ({
	root: {

	},
	slide: {
		maxWidth: 320,
		marginLeft: 5,
		marginRight: 5,
		[theme.breakpoints.up('sm')]: {
			maxWidth: 400
		},
		'&:first-child': {
			marginLeft: 0
		},
		'&:last-child': {
			marginRight: 0
		}
	}
}));

type ClickActionPayload = {
	toolCode: string;
	ctaType: string;
	eventLabel: string;
	href: string;
};

export type EmobilityNavigationClasses = Partial<ReturnType<typeof useStyles>['classes']> & MappedTileClassNames;

export type EmobilityNavigationProps = {
	toolCodes: string[];
	resourceProvider: (code: string, defaultText: string, namespace: string) => string;
	clgEndpoint: string;
	bid: string;
	culture: string;
	dealer?: string;
	onTileClick?: (payload: ClickActionPayload) => void;
	classes?: EmobilityNavigationClasses;
};

export const EmobilityNavigation = (props: EmobilityNavigationProps) => {
	const { toolCodes, resourceProvider, clgEndpoint, bid, culture, dealer, onTileClick, classes } = props;
	const { slide, root, ...tileClasses } = classes ?? {};
	const buildClgEmobilityLink = useBuildEmobilityClgLink();

	let { classes: styles } = useStyles(undefined, { props });

	const r = useResponsive();
	const toolCodesCount = toolCodes?.length ?? 0;
	const toolCodesCountValid = toolCodesCount === 2 || toolCodesCount === 3;

	if (!toolCodesCountValid) {
		console.error('Invalid number of tool, navigation wont render. Provide 2 or 3 tool in toolCodes list.');
		return null;
	}

	const handleClick = (toolCode: string, href: string, eventLabel: string) => {
		onTileClick?.({ toolCode, ctaType, eventLabel, href });
		window.open(href, '_blank');
	};

	const gridSize = Math.max(12 / toolCodes.length, 4) as GridSize;
	const tileSize = r<'small' | 'large'>({ xs: 'small', md: toolCodes.length >= 3 ? 'small' : 'large' });

	return (
		<TilesContainer
			modeBreakpoint='md'
			swiperModeProps={{
				spaceBetween: 0,
				slidesPerView: 'auto',
				classes: { slide: styles.slide, root: styles.root },
				centeredSlidesBounds: true,
				centeredSlides: true
			}}
			gridModeProps={{
				className: styles.root,
				gridSizes: { xs: 12, md: gridSize }
			}}>
			{toolCodes.map(toolCode =>
				<EmobilityNavigationTile
					href={buildClgEmobilityLink(clgEndpoint, toolCode, bid, culture, dealer)}
					key={toolCode}
					toolCode={toolCode}
					title={resourceProvider('EmobilityNavigationTitle.' + toolCode, null, commonNamespace)}
					textButton={resourceProvider('EmobilityNavigationButton', null, commonNamespace)}
					size={tileSize}
					onClick={handleClick}
					classes={resolveTileClasses(tileClasses)} />)}
		</TilesContainer>
	);
};

const resolveTileClasses = (classes: MappedTileClassNames): EmobilityNavigationTileClasses => ({
	root: classes?.tileRoot,
	icon: classes?.tileIcon,
	panelTitle: classes?.tilePanelTitle,
	title: classes?.tileTitle,
	linkRoot: classes?.tileLinkRoot,
	linkIcon: classes?.tileLinkIcon
});
